import React, { useEffect, useState } from 'react';
import { Button, Card, CardHeader, CardFooter, CardTitle, CardContent, } from '@cargomatic/ui';
import Label from 'components/v2/forms/Label';
import Tooltip from 'components/ui/Tooltip';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { findUsers } from 'utils/UserWebAPIUtils';
import { alreadyConfirmedLaneRatesV3, updateLaneRatesWithRateConditions, getLaneRatesWithRateConditions, } from 'utils/AccessorialRequestWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import { validateDocumentFilesSizes, isPDFEncrypted } from 'utils/documents';
import moment from 'moment-timezone';
import { FileUploader } from 'components/v2/FileUploader';
import { useRateConditionTypes } from 'hooks/useLaneRate';
import Alert from 'components/ui/Alert';
import _, { omitBy, isNil } from 'lodash';
import { CirclePlus, Trash, Eraser } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { createErrorMessage, laneRatesMapper, mapLaneRatesPayload, } from './lane-rates-v2-helpers';
import LaneRateForm from './LaneRateForm';
const emptyLaneRate = {
    customerId: null,
    customerType: null,
    customerName: null,
    originZone: null,
    originZoneName: null,
    originState: null,
    originCity: null,
    originZip: null,
    destinationZone: null,
    destinationZoneName: null,
    destinationState: null,
    destinationCity: null,
    destinationZip: null,
    reversible: null,
    weeklyCommitment: null,
    rate: null,
    effectiveStart: null,
    effectiveEnd: null,
    eiaRegion: null,
    fscConfigId: null,
    alreadyConfirmed: false,
    overrideCustomerFsc: false,
    rateConditions: [],
    key: uuidv4(),
};
export default function LaneRatesEditor({ className, isActionCreate }) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [saveDisabled, setSaveDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [savingFscConfigs, setSavingFscConfigs] = useState([]);
    const history = useHistory();
    const [files, setFiles] = useState([]);
    const [confirmed, setConfirmed] = useState(false);
    const [laneRates, setLaneRates] = useState([emptyLaneRate]);
    const location = useLocation();
    const [fileRequiredError, setFileRequiredError] = useState(false);
    const [fileEncryptedError, setFileEncryptedError] = useState(false);
    const [saveErrors, setSaveErrors] = useState([]);
    const [formValid, setFormValid] = useState(true);
    const { data } = useRateConditionTypes();
    const rateConditionTypes = data || [];
    const action = isActionCreate ? 'create' : 'update';
    useEffect(() => {
        if (!saving) {
            return;
        }
        const allSaved = savingFscConfigs.every((saving) => !saving);
        if (allSaved && saving) {
            continueSaving();
        }
    }, [savingFscConfigs]);
    useEffect(() => {
        async function fetchData() {
            const result = await getLaneRatesWithRateConditions(location.search);
            const data = result.data;
            const { laneRates } = data;
            const mappedLaneRates = await laneRatesMapper(laneRates);
            setLaneRates(mappedLaneRates);
            setLoading(false);
        }
        if (action === 'update') {
            fetchData();
        }
        else {
            setLaneRates([emptyLaneRate]);
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        validateFileUpload(files).then((valid) => setSaveDisabled(!valid || !formValid));
    }, [files]);
    const handleSave = async () => {
        setSaveErrors([]);
        if (saving) {
            return;
        }
        setSaving(true);
        setSavingFscConfigs(laneRates.map(() => true));
        // continueSaving() will be called after all fsc configs are saved
        // savingFscConfigs[] state triggers child forms to validate form then save FSC configs then call onSaveSuccess or onSaveFail
    };
    const continueSaving = async () => {
        var _a, _b, _c;
        if (!saving) {
            return;
        }
        if (confirmed) {
            try {
                validateDocumentFilesSizes(files);
            }
            catch (e) {
                console.error('Error validating document files sizes', e);
                return;
            }
        }
        else {
            laneRates.map((laneRate) => {
                delete laneRate.document;
                return Object.assign({}, laneRate);
            });
        }
        let laneRatesToSave = await addCustomerEmails(laneRates);
        // Change lane rate objects format to fit old API schema
        laneRatesToSave = laneRatesToSave.map((laneRate) => {
            const newLaneRate = Object.assign(Object.assign({}, laneRate), { alreadyConfirmed: confirmed, reversible: laneRate.reversible ? 'Yes' : 'No' });
            // Change date format to send like in old flow
            newLaneRate.effectiveStart = moment
                .utc(newLaneRate.effectiveStart)
                .format();
            newLaneRate.effectiveEnd = moment.utc(newLaneRate.effectiveEnd).format();
            return newLaneRate;
        });
        laneRatesToSave = mapLaneRatesPayload(laneRatesToSave, action);
        try {
            const response = confirmed
                ? await alreadyConfirmedLaneRatesV3(laneRatesToSave, files, action)
                : await updateLaneRatesWithRateConditions(laneRatesToSave, action);
            if (((_a = response === null || response === void 0 ? void 0 : response.entity) === null || _a === void 0 ? void 0 : _a.success) || ((_b = response === null || response === void 0 ? void 0 : response.body) === null || _b === void 0 ? void 0 : _b.success)) {
                showToast(t('REQUEST_SUCCESS'), t('LANE_RATE_SUBMIT_SUCCESS'), 'success');
                goToLaneRates();
            }
            else {
                showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
                setSaving(false);
            }
        }
        catch (error) {
            setSaveErrors((_c = error.body) === null || _c === void 0 ? void 0 : _c.validationErrors);
            showToast(t('REQUEST_ERROR'), t('LANE_RATE_UPDATE_ERROR'), 'error');
            setSaving(false);
        }
    };
    const addCustomerEmails = async (laneRates) => {
        var _a;
        const customerIds = laneRates.map((laneRate) => laneRate.customerId);
        const findUsersResponse = await findUsers({ _id: { $in: customerIds } });
        const customers = (_a = findUsersResponse === null || findUsersResponse === void 0 ? void 0 : findUsersResponse.entity) === null || _a === void 0 ? void 0 : _a.items;
        if (!customers) {
            return laneRates;
        }
        return laneRates.map((laneRate) => {
            const customer = customers.find((customer) => customer.id === laneRate.customer);
            return Object.assign(Object.assign({}, laneRate), { customerEmail: customer === null || customer === void 0 ? void 0 : customer.email });
        });
    };
    const renderHeader = () => {
        const title = location.pathname.includes('add')
            ? t('ADD_LANE_RATE')
            : t('EDIT_LANE_RATES');
        return (React.createElement(CardHeader, null,
            React.createElement("div", { className: "flex items-center justify-between" },
                React.createElement(CardTitle, null, title),
                isActionCreate && (React.createElement(Button, { onClick: handleAddLaneRate, variant: "outline", className: "" },
                    "Add Lane Rate",
                    React.createElement(CirclePlus, { className: "w-3.5 h-3.5 ml-2" }))))));
    };
    const uploadConfirmationDocuments = () => {
        if (confirmed) {
            return null;
        }
        return (React.createElement(Button, { onClick: () => setConfirmed(true), testDataId: "lane-rate-save-and-upload-confirmation" }, t('SAVE_AND_UPLOAD_CONFIRMATION')));
    };
    const onCancelClick = () => {
        setFileRequiredError(false);
        return confirmed ? setConfirmed(false) : goToLaneRates();
    };
    const renderFooter = () => {
        const actionLabel = t('SAVE_AND_COMPLETE_UPLOAD_CONFIRMATION');
        return (React.createElement(CardFooter, { className: "flex items-center justify-end space-x-4 bg-gray-50 pt-6" },
            uploadConfirmationDocuments(),
            confirmed && (React.createElement(Button, { variant: "outline", onClick: handleSave, testDataId: `lane-rate-${actionLabel}`, disabled: saveDisabled }, actionLabel)),
            React.createElement(Button, { color: "", variant: "outline", onClick: onCancelClick, testDataId: "lane-rate-cancel-button" }, t('CANCEL'))));
    };
    const goToLaneRates = () => {
        var _a;
        const listQueryFilterParameters = (_a = location.state) === null || _a === void 0 ? void 0 : _a.listQueryFilterParameters;
        if (listQueryFilterParameters) {
            history.push('/lane-rates', { listQueryFilterParameters });
        }
        else {
            history.push('/lane-rates');
        }
    };
    const handleAddLaneRate = () => {
        const lastEdited = _.last(laneRates);
        const key = uuidv4();
        setLaneRates([...laneRates, Object.assign(Object.assign(Object.assign({}, emptyLaneRate), lastEdited), { key })]);
    };
    const handleClearLaneRate = (key) => {
        // Forcing render by changing the key
        setLaneRates((laneRates) => {
            const newLaneRates = laneRates.map((laneRate) => {
                if (laneRate.key === key) {
                    return Object.assign(Object.assign({}, emptyLaneRate), { key: uuidv4() });
                }
                return laneRate;
            });
            return newLaneRates;
        });
    };
    const handleRemoveLaneRate = (i) => {
        setLaneRates([...laneRates.slice(0, i), ...laneRates.slice(i + 1)]);
    };
    const handleChangeLaneRate = (index, laneRate) => {
        const newLaneRates = [...laneRates];
        const newLaneRate = omitBy(laneRate, isNil);
        newLaneRates[index] = newLaneRate;
        setLaneRates(newLaneRates);
        setSaveErrors([]);
    };
    const validateFileUpload = async () => {
        if (confirmed && files.length === 0) {
            setFileRequiredError(true);
            return false;
        }
        const anyFilesEncryptedPDFs = (await Promise.all(files.map(isPDFEncrypted))).some((isEncrypted) => isEncrypted);
        if (anyFilesEncryptedPDFs) {
            setFileEncryptedError(true);
            return false;
        }
        setFileRequiredError(false);
        setFileEncryptedError(false);
        return true;
    };
    const handleValidation = async (index, valid) => {
        const filesValid = await validateFileUpload();
        setFormValid(valid);
        if (!valid || !filesValid) {
            setSaving(false);
            setSavingFscConfigs([]);
            setSaveDisabled(true);
            return;
        }
        setSaveDisabled(false);
        if (!savingFscConfigs[index]) {
            return;
        }
        setSavingFscConfigs((prevState) => {
            const newSavingFscConfigs = [...prevState];
            newSavingFscConfigs[index] = false;
            return newSavingFscConfigs;
        });
    };
    const renderFileInput = () => {
        return (React.createElement("div", { className: "py-6" },
            React.createElement(Label, { className: "mb-4" }, t('UPLOAD_CONFIRMATION')),
            React.createElement(FileUploader, { onChange: (files) => {
                    setFiles(files);
                    setFileRequiredError(false);
                    setFileEncryptedError(false);
                }, fileTypesLabel: `${t('PDF')}, ${t('IMAGE')}`, multiple: true }),
            fileRequiredError && (React.createElement("span", { "data-testid": "errorLabelFile", className: "block text-xs italic text-red-500 mt-1" }, t('FILE_REQUIRED'))),
            fileEncryptedError && (React.createElement("span", { "data-testid": "errorLabelFile", className: "block text-xs italic text-red-500 mt-1" }, t('PDF_DOCUMENT_ENCRYPTED')))));
    };
    const renderSaveErrors = (saveErrors) => {
        if (!saveErrors || !saveErrors.length) {
            return null;
        }
        return (React.createElement(Alert, { type: "error", title: `${t('PLEASE_ADDRESS_THE_FOLLOWING_ISSUES')}`, canClose: true, onClose: () => setSaveErrors([]), className: "mb-4" },
            React.createElement("ul", { className: "list-disc space-y-1 pl-5" }, saveErrors.map((error, i) => (React.createElement("li", { key: `save-errors-${i}` }, createErrorMessage(error)))))));
    };
    if (loading) {
        return React.createElement("div", { className: "w-full animate-pulse" }, "Loading...");
    }
    return (React.createElement("div", { className: "w-full" },
        React.createElement(Card, { className: className },
            renderHeader(),
            React.createElement(CardContent, null,
                renderSaveErrors(saveErrors),
                React.createElement("div", { className: "divide-y divide-gray-200" },
                    laneRates.map((laneRate, i) => (React.createElement("div", { className: "py-4", key: isActionCreate ? laneRate.key : laneRate.id },
                        React.createElement("div", { className: "mb-4 flex items-center space-x-3" },
                            React.createElement("h3", { className: "" },
                                t('LANE_RATE'),
                                " ",
                                i + 1),
                            isActionCreate && (React.createElement("div", { className: "flex" },
                                React.createElement(Tooltip, { dataTestId: `Tooltip-rateconditionadd-${1}`, id: 1, content: t('DELETE_LANE_RATE'), tooltipClassName: "" },
                                    React.createElement(Button, { variant: "icon", onClick: () => handleRemoveLaneRate(i), disabled: i === 0 },
                                        React.createElement(Trash, { className: "w-3.5 h-3.5" }))),
                                React.createElement(Tooltip, { dataTestId: `Tooltip-rateconditionremove-${2}`, id: 2, content: t('CLEAR_LANE_RATE_FIELDS'), tooltipClassName: "" },
                                    React.createElement(Button, { variant: "icon", onClick: () => handleClearLaneRate(laneRate.key), className: "px-0" },
                                        React.createElement(Eraser, { className: "w-3.5 h-3.5" })))))),
                        React.createElement(LaneRateForm, { isActionCreate: isActionCreate, initialLaneRates: null, laneRate: laneRate, onChange: (laneRate) => handleChangeLaneRate(i, laneRate), saving: saving, onValidate: (valid) => handleValidation(i, valid), rateConditionTypes: rateConditionTypes })))),
                    confirmed && renderFileInput())),
            renderFooter())));
}
